// =============================================
// pageGrouping — where does each document start and end inside one PDF?
// =============================================
// A scanned batch often mixes single-page receipts with invoices that run over
// several pages. Given the text of every page (text layer or OCR), this decides,
// for each pair of neighbouring pages, whether the second page CONTINUES the
// first or STARTS a new document — and how sure it is. Pure functions, no pdf.js:
// the reader lives in pdfTextExtractor.ts (readPdfPageTexts).
//
// The answer is a suggestion. The Upload Docs "check the split" step shows it on
// page thumbnails and the user has the last word; `confident: false` marks the
// boundaries worth a look.
// =============================================

export interface PageInfo {
  /** The page's text, one line per printed line where the reader could tell. */
  text: string;
  /** Page size in PDF points — a change of paper size usually means a new document. */
  width?: number;
  height?: number;
}

/** The decision between one page and the next. */
export interface PageBoundary {
  /** true = the later page starts a new document; false = it continues the earlier one. */
  startsNew: boolean;
  /** false = a guess the user should check. */
  confident: boolean;
  /** Why, in plain words (shown as a tooltip). */
  reason: string;
}

export interface SplitPlan {
  pageCount: number;
  /** boundaries[i] sits between page i and page i+1 (0-based) — pageCount-1 entries. */
  boundaries: PageBoundary[];
  /** Page indexes (0-based) of each document, in order. */
  groups: number[][];
}

// Fewer readable characters than this = a blank page (the back of a duplex scan).
const BLANK_PAGE_CHARS = 15;
// A page this full with no total on it is a page of line items, not a small receipt.
const FULL_PAGE_CHARS = 200;
// How many lines count as the "top" / "bottom" of a page.
const EDGE_LINES = 8;

interface PageSignals {
  blank: boolean;
  chars: number;
  pageNo: number | null;
  pageTotal: number | null;
  ref: string;
  topContinues: boolean;     // "continued", "balance forward" near the top
  bottomContinues: boolean;  // "continued on next page" near the bottom
  hasEnd: boolean;           // a total / amount due / thank-you
  hasTitle: boolean;         // "Invoice", "Receipt" … near the top
}

function linesOf(text: string): string[] {
  return text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
}

// "Page 2 of 3", "Pg 2/3", "Page: 2"; a bare "2 of 3" only at the page's edge
// (in the body it is more likely "2 of 3 items").
function readPageMarker(text: string, edges: string): { no: number | null; total: number | null } {
  const valid = (no: number, total: number) => no >= 1 && total >= no && total <= 500;
  const full = text.match(/\b(?:page|pg)\b\.?\s*[:#]?\s*(\d{1,3})\s*(?:of|\/)\s*(\d{1,3})\b/i);
  if (full) {
    const no = parseInt(full[1], 10), total = parseInt(full[2], 10);
    if (valid(no, total)) return { no, total };
  }
  const bare = edges.match(/(?:^|\s)(\d{1,3})\s+of\s+(\d{1,3})(?:\s|$)/im);
  if (bare) {
    const no = parseInt(bare[1], 10), total = parseInt(bare[2], 10);
    if (valid(no, total)) return { no, total };
  }
  const single = text.match(/\b(?:page|pg)\b\.?\s*[:#]?\s*(\d{1,3})\b/i);
  if (single) {
    const no = parseInt(single[1], 10);
    if (no >= 1) return { no, total: null };
  }
  return { no: null, total: null };
}

// The document's own number. Account / customer numbers are deliberately left out:
// two different bills from one supplier share them.
// A label ("No", "#", "Number") is required, so "Invoice Date 2024-01-05" is not a number.
const REF_PATTERN = /\b(?:invoice|inv|receipt|order|bill|statement|quote|estimate|transaction|trans|confirmation|reference|ref)\b\.?\s*(?:#|no\b\.?|number\b|num\b\.?|id\b)\s*[:.#]?\s*([A-Z0-9][\w-]{2,20})/gi;

function readDocRef(text: string): string {
  for (const m of Array.from(text.matchAll(REF_PATTERN))) {
    if (/\d/.test(m[1])) return m[1].toUpperCase();
  }
  return '';
}

function signalsOf(page: PageInfo): PageSignals {
  const text = page.text || '';
  const lines = linesOf(text);
  const top = lines.slice(0, EDGE_LINES).join('\n');
  const bottom = lines.slice(-EDGE_LINES).join('\n');
  const chars = text.replace(/\s/g, '').length;
  const marker = readPageMarker(text, `${top}\n${bottom}`);
  // "Subtotal" and "page total" are not the end of a document.
  const withoutPartTotals = text.replace(/\b(?:sub[\s-]?total|page\s+total)\b/gi, ' ');
  const NEXT_PAGE = /\b(?:continued\s+(?:on|next|overleaf)|carried\s+forward|see\s+next\s+page|continues?\s+on)\b/gi;
  return {
    blank: chars < BLANK_PAGE_CHARS,
    chars,
    pageNo: marker.no,
    pageTotal: marker.total,
    ref: readDocRef(text),
    // On a short page top and bottom overlap — "continued on next page" is not "continued from".
    topContinues: /\b(?:continued|cont'?d|brought\s+forward|balance\s+(?:brought\s+)?forward)\b/i.test(top.replace(NEXT_PAGE, ' ')),
    bottomContinues: bottom.search(NEXT_PAGE) >= 0,
    hasEnd: /\b(?:total|amount\s+due|balance\s+due|amount\s+paid|change\s+due|thank\s+you)\b/i.test(withoutPartTotals),
    hasTitle: /\b(?:invoice|receipt|statement|purchase\s+order|sales\s+order|quote|quotation|estimate|credit\s+(?:note|memo)|packing\s+slip|bill\s+of\s+sale)\b/i.test(top),
  };
}

function sizeDiffers(a: PageInfo, b: PageInfo): boolean {
  if (!a.width || !a.height || !b.width || !b.height) return false;
  const off = (x: number, y: number) => Math.abs(x - y) / Math.max(x, y) > 0.12;
  return off(a.width, b.width) || off(a.height, b.height);
}

function decide(prevPage: PageInfo, prev: PageSignals, curPage: PageInfo, cur: PageSignals): PageBoundary {
  const join = (reason: string, confident = true): PageBoundary => ({ startsNew: false, confident, reason });
  const cut = (reason: string, confident = true): PageBoundary => ({ startsNew: true, confident, reason });

  // 1. Printed page numbers are the strongest evidence.
  if (cur.pageNo === 1) return cut('This page is marked "page 1".');
  if (cur.pageNo != null && cur.pageNo > 1) {
    if (prev.pageNo === cur.pageNo - 1) return join(`Marked page ${cur.pageNo}, right after page ${prev.pageNo}.`);
    if (prev.pageNo == null) return join(`This page is marked "page ${cur.pageNo}".`, false);
    return cut(`Page numbers do not follow on (${prev.pageNo} then ${cur.pageNo}).`, false);
  }
  if (prev.pageNo != null && prev.pageTotal != null) {
    if (prev.pageNo < prev.pageTotal) return join(`The page before is ${prev.pageNo} of ${prev.pageTotal}.`, false);
    return cut(`The page before is the last one (${prev.pageNo} of ${prev.pageTotal}).`);
  }

  // 2. The document's own number.
  if (prev.ref && cur.ref) {
    return prev.ref === cur.ref
      ? join(`Same document number (${cur.ref}) on both pages.`)
      : cut(`Different document numbers (${prev.ref} and ${cur.ref}).`);
  }

  // 3. A different paper size (a till receipt next to a letter page).
  if (sizeDiffers(prevPage, curPage)) return cut('The page size changes here.');

  // 4. "Continued" wording.
  if (cur.topContinues) return join('This page says it is continued from another.');
  if (prev.bottomContinues) return join('The page before says it continues.');

  // 5. A full page with no total, followed by a page with no heading of its own.
  if (!prev.hasEnd && prev.chars >= FULL_PAGE_CHARS && !cur.hasTitle && !cur.ref) {
    return join('The page before has no total, and this page has no heading of its own.', false);
  }

  // 6. Otherwise each page is its own document — the common case for receipts.
  const clear = prev.hasEnd && (cur.hasTitle || !!cur.ref);
  return cut(
    clear ? 'The page before ends with a total and this page has its own heading.'
          : 'Nothing links this page to the one before.',
    clear,
  );
}

/** Turn "starts a new document" flags (one per boundary) into page groups. */
export function groupsFromCuts(pageCount: number, cuts: boolean[]): number[][] {
  const groups: number[][] = [];
  for (let i = 0; i < pageCount; i++) {
    if (i === 0 || cuts[i - 1]) groups.push([i]);
    else groups[groups.length - 1].push(i);
  }
  return groups;
}

/** Suggest how a PDF's pages fall into documents. */
export function planDocumentSplit(pages: PageInfo[]): SplitPlan {
  const signals = pages.map(signalsOf);
  const boundaries: PageBoundary[] = [];
  // Blank pages carry no evidence: compare each page with the last one that had text.
  let lastWithText = 0;
  for (let i = 1; i < pages.length; i++) {
    if (signals[i].blank) {
      boundaries.push({ startsNew: false, confident: false, reason: 'Blank page — kept with the page before.' });
      continue;
    }
    if (signals[lastWithText].blank) {
      // Nothing readable before this page (the file opens with blank pages).
      boundaries.push({ startsNew: true, confident: false, reason: 'The page before is blank.' });
    } else {
      boundaries.push(decide(pages[lastWithText], signals[lastWithText], pages[i], signals[i]));
    }
    lastWithText = i;
  }
  return {
    pageCount: pages.length,
    boundaries,
    groups: groupsFromCuts(pages.length, boundaries.map(b => b.startsNew)),
  };
}
